import supabase from "@/features/supabase";
import type { Place, Summary, ValueType } from "@/tools/data-visualisation/climate-metrics/types";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { decompress } from "fzstd";
import { splitFrames } from "./frames";

/**
 * The climate metrics Worker, served on Hub's own hostname under /climate-metrics-data/ (in
 * development, next.config.mjs proxies that path to `yarn dev` in services/climate-metrics/worker).
 * Every route needs the caller's Supabase session.
 */
export const CLIMATE_METRICS_BASE = "/climate-metrics-data/v1/";

const authorisation = async (): Promise<string | undefined> => {
  const { data } = await supabase.auth.getSession();
  return data.session ? `Bearer ${data.session.access_token}` : undefined;
};

/*
 * The Worker answers 410 for a build it no longer serves, after a deploy that changed the build
 * layout. Retrying never helps: the page has to reload to learn the new build id.
 */
let retired = false;
const retiredListeners = new Set<() => void>();

function noteStatus(status: number | string | undefined) {
  if (status !== 410 || retired) return;
  retired = true;
  retiredListeners.forEach((listener) => listener());
}

/** Whether a request was answered 410: this page's build is no longer served. */
export const isClimateMetricsBuildRetired = () => retired;

export function subscribeClimateMetricsBuildRetired(listener: () => void): () => void {
  retiredListeners.add(listener);
  return () => retiredListeners.delete(listener);
}

const baseQuery = fetchBaseQuery({
  baseUrl: CLIMATE_METRICS_BASE,
  prepareHeaders: async (headers) => {
    const value = await authorisation();
    if (value) headers.set("authorization", value);
    return headers;
  },
});

export const climateMetricsApi = createApi({
  reducerPath: "climateMetrics",
  baseQuery: async (...args: Parameters<typeof baseQuery>) => {
    const result = await baseQuery(...args);
    noteStatus(result.error?.status);
    return result;
  },
  // Everything below a build id is immutable, so keep answers for the whole visit.
  keepUnusedDataFor: 60 * 60,
  endpoints: (build) => ({
    /** The build this deployment serves. */
    getClimateMetricsBuild: build.query<{ build: string }, void>({ query: () => "build", keepUnusedDataFor: 60 }),
    getClimateMetricsSummary: build.query<Summary, string>({ query: (id) => `${encodeURIComponent(id)}/summary` }),
    getClimateMetricsPlace: build.query<Place, { build: string; lat: number; lon: number }>({
      query: ({ build, lat, lon }) => ({ url: `${encodeURIComponent(build)}/place`, params: { lat, lon } }),
    }),
  }),
});

export const { useGetClimateMetricsBuildQuery, useGetClimateMetricsSummaryQuery, useGetClimateMetricsPlaceQuery } =
  climateMetricsApi;

/*
 * Map layers are fetched outside the Redux store: a layer is a megabyte of float32s, which the
 * store's serialisability checks would walk on every action. Both caches keep the most recently
 * used entries only, so a long visit does not hold every layer it has shown.
 */

class Recent<V> {
  private readonly entries = new Map<string, V>();
  constructor(private readonly size: number) {}

  get(key: string, make: () => V): V {
    let value = this.entries.get(key);
    if (value === undefined) value = make();
    this.entries.delete(key);
    this.entries.set(key, value);
    if (this.entries.size > this.size) this.entries.delete(this.entries.keys().next().value!);
    return value;
  }

  /** The entry under `key`, now the most recently used, or undefined without making one. */
  touch(key: string): V | undefined {
    const value = this.entries.get(key);
    if (value !== undefined) this.get(key, () => value);
    return value;
  }

  delete(key: string) {
    this.entries.delete(key);
  }
}

async function getBytes(path: string): Promise<ArrayBuffer> {
  const value = await authorisation();
  const response = await fetch(CLIMATE_METRICS_BASE + path, { headers: value ? { authorization: value } : {} });
  noteStatus(response.status);
  if (!response.ok) throw new Error(`climate metrics ${path}: HTTP ${response.status}`);
  return response.arrayBuffer();
}

function cached<V>(cache: Recent<Promise<V>>, path: string, make: () => Promise<V>): Promise<V> {
  const promise = cache.get(path, make);
  promise.catch(() => cache.delete(path));
  return promise;
}

const layers = new Recent<Promise<Float32Array>>(24);

/** One global layer, rows from south to north, NaN where there is no value. */
export function fetchClimateMetricsLayer(
  build: string,
  metric: string,
  valueType: ValueType,
  horizon: string,
  scenario: string
): Promise<Float32Array> {
  const path = [build, "layer", metric, valueType, horizon, scenario].map(encodeURIComponent).join("/");
  return cached(layers, path, async () => new Float32Array(await getBytes(path)));
}

/** Every horizon of one metric, value type and scenario: the time slider's layers. */
export class Slider {
  private readonly decoded = new Recent<Float32Array | null>(4);
  constructor(private readonly frames: (Uint8Array | null)[]) {}

  /** Horizon `k`'s layer, rows from south to north, or null where there is none. Decoded on first use. */
  layer(k: number): Float32Array | null {
    return this.decoded.get(String(k), () => {
      const frame = this.frames[k];
      if (!frame) return null;
      const bytes = decompress(frame);
      return new Float32Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 4);
    });
  }
}

// A slider is 3-7 MB compressed; a dozen covers flicking between a few metrics and scenarios.
const sliders = new Recent<Promise<Slider>>(12);
// The slider each request gave, once it has arrived; it leaves with its request.
const arrived = new WeakMap<Promise<Slider>, Slider>();

const sliderPath = (build: string, metric: string, valueType: ValueType, scenario: string) =>
  [build, "layers", metric, valueType, scenario].map(encodeURIComponent).join("/");

export function fetchClimateMetricsSlider(
  build: string,
  metric: string,
  valueType: ValueType,
  scenario: string
): Promise<Slider> {
  const path = sliderPath(build, metric, valueType, scenario);
  return cached(sliders, path, () => {
    const promise: Promise<Slider> = getBytes(path).then((body) => {
      const slider = new Slider(splitFrames(body));
      arrived.set(promise, slider);
      return slider;
    });
    return promise;
  });
}

/** The slider if it has arrived, so a drag along the slider can redraw without waiting a tick. */
export function loadedClimateMetricsSlider(build: string, metric: string, valueType: ValueType, scenario: string) {
  // Showing it counts as using it, so the slider on screen is the last to leave.
  const promise = sliders.touch(sliderPath(build, metric, valueType, scenario));
  return promise && arrived.get(promise);
}
